import { useState, useEffect, useCallback } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Calendar, CheckCircle, ArrowLeft, Sparkles } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import logger from "@/lib/logger";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const EventSelector = ({ loading, events, value, onChange }) => {
  if (loading) {
    return <div className="h-10 bg-gray-50 animate-pulse rounded-xl"></div>;
  }
  if (events.length === 0) {
    return (
      <p className="text-soar-blue/50 text-sm p-4 bg-gray-50 rounded-xl">
        No events currently open for registration. Check back soon!
      </p>
    );
  }
  return (
    <Select value={value} onValueChange={onChange}>
      <SelectTrigger className="rounded-xl" data-testid="event-select">
        <SelectValue placeholder="Choose an event..." />
      </SelectTrigger>
      <SelectContent>
        {events.map(event => (
          <SelectItem key={event.id} value={event.id}>
            {event.title} - {new Date(event.date + 'T00:00:00').toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
          </SelectItem>
        ))}
      </SelectContent>
    </Select>
  );
};

const WorkshopRegistration = () => {
  const [searchParams] = useSearchParams();
  const eventId = searchParams.get('event');
  const eventTitle = searchParams.get('title') || 'Workshop';
  
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [submitting, setSubmitting] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [formData, setFormData] = useState({
    event_id: eventId || "",
    event_title: eventTitle || "",
    first_name: "",
    last_name: "",
    email: "",
    phone: "",
    age: "",
    school_or_occupation: "",
    special_needs: "",
    how_heard: ""
  });

  const fetchEvents = useCallback(async () => {
    try {
      const response = await axios.get(`${API}/calendar`);
      const upcomingEvents = response.data
        .filter(e => e.registration_open && !e.is_deadline)
        .sort((a, b) => new Date(a.date) - new Date(b.date));
      setEvents(upcomingEvents);
      
      if (eventId) {
        const selectedEvent = response.data.find(e => e.id === eventId);
        if (selectedEvent) {
          setFormData(prev => ({
            ...prev,
            event_id: selectedEvent.id,
            event_title: selectedEvent.title
          }));
        }
      }
    } catch (error) {
      logger.error("Failed to fetch events:", error);
    } finally {
      setLoading(false);
    }
  }, [eventId]);

  useEffect(() => {
    fetchEvents();
  }, [fetchEvents]);

  const handleChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };

  const handleEventChange = (eventId) => {
    const selectedEvent = events.find(e => e.id === eventId);
    setFormData(prev => ({
      ...prev,
      event_id: eventId,
      event_title: selectedEvent?.title || ""
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!formData.event_id) {
      toast.error("Please select an event");
      return;
    }
    
    setSubmitting(true);

    try {
      const submitData = {
        ...formData,
        age: parseInt(formData.age)
      };

      await axios.post(`${API}/workshop-registration`, submitData);
      setSubmitted(true);
      toast.success("Registration submitted successfully!");
    } catch (error) {
      const message = error.response?.data?.detail || "Failed to submit registration";
      toast.error(message);
    } finally {
      setSubmitting(false);
    }
  };

  if (submitted) {
    return (
      <div className="min-h-[60vh] flex items-center justify-center py-20" data-testid="registration-success">
        <Card className="max-w-lg mx-auto rounded-2xl border border-gray-100 shadow-ambient">
          <CardContent className="p-10 text-center">
            <div className="w-20 h-20 mx-auto mb-6 rounded-full bg-soar-teal/10 flex items-center justify-center">
              <CheckCircle className="w-10 h-10 text-soar-teal" />
            </div>
            <h2 className="font-heading font-bold text-2xl text-soar-blue mb-4">
              Registration Submitted!
            </h2>
            <p className="text-muted-foreground mb-6">
              Thank you for registering for <strong>{formData.event_title}</strong>. 
              We'll be in touch with more details soon!
            </p>
            <div className="flex flex-col gap-3">
              <Link to="/calendar">
                <Button className="w-full rounded-full bg-soar-teal hover:bg-soar-teal/90">
                  View More Events
                </Button>
              </Link>
              <Link to="/">
                <Button variant="outline" className="w-full rounded-full">
                  Back to Home
                </Button>
              </Link>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div data-testid="workshop-registration-page">
      {/* Hero Section */}
      <section className="relative py-24 bg-soar-teal text-white overflow-hidden">
        <div className="absolute inset-0 bg-gradient-to-br from-soar-teal via-soar-teal to-soar-blue/50" />
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 relative z-10">
          <Link to="/calendar" className="inline-flex items-center gap-2 text-white/80 hover:text-white mb-6 transition-colors">
            <ArrowLeft className="w-4 h-4" />
            Back to Calendar
          </Link>
          <div className="max-w-3xl">
            <div className="inline-flex items-center gap-2 bg-white/20 text-white px-4 py-2 rounded-full text-sm font-medium mb-6">
              <Sparkles className="w-4 h-4" />
              Event Registration
            </div>
            <h1 className="font-heading font-bold text-4xl sm:text-5xl leading-tight mb-4">
              Register for an Event
            </h1>
            <p className="text-lg text-white/90">
              Sign up for our workshops, programs, and community events. 
              Your registration will be sent directly to our team!
            </p>
          </div>
        </div>
      </section>

      {/* Registration Form */}
      <section className="py-16 bg-white">
        <div className="max-w-2xl mx-auto px-4 sm:px-6 lg:px-8">
          <Card className="rounded-2xl border border-gray-100 shadow-ambient">
            <CardContent className="p-8 sm:p-10">
              <form onSubmit={handleSubmit} className="space-y-6">
                {/* Event Selection */}
                <div className="space-y-2">
                  <Label htmlFor="event">Select Event *</Label>
                  <EventSelector loading={loading} events={events} value={formData.event_id} onChange={handleEventChange} />
                </div>

                {/* Personal Information */}
                <div className="pt-4 border-t">
                  <h3 className="font-heading font-bold text-lg text-soar-blue mb-4">Your Information</h3>
                  <div className="grid sm:grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label htmlFor="first_name">First Name *</Label>
                      <Input
                        id="first_name"
                        name="first_name"
                        value={formData.first_name}
                        onChange={handleChange}
                        required
                        className="rounded-xl"
                        data-testid="reg-first-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="last_name">Last Name *</Label>
                      <Input
                        id="last_name"
                        name="last_name"
                        value={formData.last_name}
                        onChange={handleChange}
                        required
                        className="rounded-xl"
                        data-testid="reg-last-name"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="email">Email *</Label>
                      <Input
                        id="email"
                        name="email"
                        type="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="rounded-xl"
                        data-testid="reg-email"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="phone">Phone *</Label>
                      <Input
                        id="phone"
                        name="phone"
                        type="tel"
                        value={formData.phone}
                        onChange={handleChange}
                        required
                        className="rounded-xl"
                        data-testid="reg-phone"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="age">Age *</Label>
                      <Input
                        id="age"
                        name="age"
                        type="number"
                        min="13"
                        max="99"
                        value={formData.age}
                        onChange={handleChange}
                        required
                        className="rounded-xl"
                        data-testid="reg-age"
                      />
                    </div>
                    <div className="space-y-2">
                      <Label htmlFor="school_or_occupation">School/Occupation</Label>
                      <Input
                        id="school_or_occupation"
                        name="school_or_occupation"
                        value={formData.school_or_occupation}
                        onChange={handleChange}
                        className="rounded-xl"
                        data-testid="reg-school"
                      />
                    </div>
                  </div>
                </div>

                {/* Additional Information */}
                <div className="space-y-4">
                  <div className="space-y-2">
                    <Label htmlFor="special_needs">Special Needs or Accommodations</Label>
                    <Textarea
                      id="special_needs"
                      name="special_needs"
                      value={formData.special_needs}
                      onChange={handleChange}
                      className="rounded-xl min-h-[80px]"
                      placeholder="Let us know if you need any accommodations..."
                      data-testid="reg-special-needs"
                    />
                  </div>
                  <div className="space-y-2">
                    <Label htmlFor="how_heard">How did you hear about us?</Label>
                    <Select
                      value={formData.how_heard}
                      onValueChange={(value) => setFormData(prev => ({ ...prev, how_heard: value }))}
                    >
                      <SelectTrigger className="rounded-xl" data-testid="reg-how-heard">
                        <SelectValue placeholder="Select an option..." />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="social-media">Social Media</SelectItem>
                        <SelectItem value="friend-family">Friend or Family</SelectItem>
                        <SelectItem value="school">School</SelectItem>
                        <SelectItem value="community-event">Community Event</SelectItem>
                        <SelectItem value="website">Website</SelectItem>
                        <SelectItem value="other">Other</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>

                <Button
                  type="submit"
                  disabled={submitting || events.length === 0}
                  className="w-full rounded-full bg-soar-gold hover:bg-soar-gold/90 text-soar-blue py-6 text-lg font-semibold"
                  data-testid="reg-submit-btn"
                >
                  {submitting ? "Submitting..." : "Submit Registration"}
                </Button>

                <p className="text-xs text-muted-foreground text-center">
                  Your registration will be sent to info@soar-solutions.org
                </p>
              </form>
            </CardContent>
          </Card>
        </div>
      </section>
    </div>
  );
};

export default WorkshopRegistration;
